import React, { useEffect, useState, useRef, useContext } from 'react';
import useAccount from '@/hooks/useAccount';
import { useRouter } from 'next/router';
import { Row, Col, Container, Card } from 'react-bootstrap';
import cloneDeep from 'lodash/cloneDeep';
import unset from 'lodash/unset';
import find from 'lodash/find';
import Collection from '@/utils/api/odas/collection';
import CollectionCard from '@/components/Collections/CollectionCard/CollectionCard';
import classes from './Collections.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@/components/Button/Button';
import { modalContext } from '@/context/modal.context';
import t from '@/utils/Language/Collection-translations';
import CollectionTypeSelectorList from './CollectionTypeSelectorList/CollectionTypeSelectorList';
import CollectionSearch from './CollectionSearch/CollectionSearch';
import { viewportContext } from '@/context/viewport.context';
import AgencyConfiguration from '@/utils/api/odas/agency_configuration';
import Team from '@/utils/api/odas/team';
import ODRS from '@/utils/api/odrs';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { hasName, displayName } from '@/utils/utils';
import { languageContext } from '@/context/language.context';
import { teamHelpers } from '@/utils/helpers';
import isEmpty from 'lodash/isEmpty';
import TablePagination from '@/components/TablePagination/TablePagination';

const defaultCollection = [];
const defaultPagination = {
  current_page: 1,
  page: 1,
  per_page: 10,
  total_count: 1,
  total_pages: 1
};
const defaultPageInfo = {
  title: 'My Collections',
  collectionFor: {
    name: '',
    url: null
  }
};

const parseIntOrFalse = (str) => {
  return typeof str === 'undefined' || isNaN(parseInt(str)) ? false : parseInt(str);
};

const Collections = ({ isFullPage = false, urlQueryParameters, ...props }) => {
  const pageRef = useRef();
  const lastRequestAt = useRef();
  const lastRequestFor = useRef({});
  const firstRender = useRef(true);
  const router = useRouter();
  const { account } = useAccount();
  const user = account.data;
  const { currentLanguage } = useContext(languageContext);
  const modal = useContext(modalContext);
  const { isMobileOrTablet, isDesktop } = useContext(viewportContext);
  const [collections, setCollections] = useState(defaultCollection);
  const [pagination, setPagination] = useState(defaultPagination);
  const [searchPage, setSearchPage] = useState(1);
  const [selectedSortBy, setSelectedSortBy] = useState(urlQueryParameters?.sortby || '');
  const [showCollectionFor, setShowCollectionFor] = useState(urlQueryParameters?.page || 'my');
  const [totalCollections, setTotalCollections] = useState('--');
  const [agencyConfiguration, setAgencyConfiguration] = useState({});
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState();
  const [organizationId, setOrganizationId] = useState(
    (['agency', 'org'].indexOf(showCollectionFor) >= 0 && parseIntOrFalse(urlQueryParameters?.organization_id)) || null
  );
  const [organization, setOrganization] = useState({});
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);
  const [loading, setLoading] = useState({ collections: true, agencies: true, teams: true, org: !!organizationId });

  const clearNewUrlParameter = () => {
    let newQuery = cloneDeep(urlQueryParameters);
    unset(newQuery, 'new');
    router.replace({
      pathname: router.pathname,
      hash: window.location.hash,
      query: newQuery
    });
  };

  useEffect(() => {
    if (urlQueryParameters.new === 'true') {
      let dataLoading = Object.keys(loading);
      let stillLoading = false;
      dataLoading.forEach((key) => {
        stillLoading = stillLoading || loading[key];
      });
      if (!stillLoading && !account.state.isLoggedIn) {
        modal.enqueueModal({
          name: 'LoginModal',
          reason: 'newCollection'
        });
        clearNewUrlParameter();
      }
    }
  }, [loading]);

  useEffect(() => {
    loadCollections(1);
  }, [router.query]);

  useEffect(() => {
    if (account.state.isLoggedIn) {
      let initPromise = new Promise((resolve) => resolve(true));
      if (user?.affiliation?.fetchable_id) {
        initPromise = loadAgencyConfiguration().then((agencyConfiguration) => {
          if (teamHelpers.userHasTeams(user, agencyConfiguration)) {
            return loadTeams().then((teams) => ({ teams, agencyConfiguration }));
          } else {
            setLoading((prevState) => ({ ...prevState, teams: false }));
            return true;
          }
        });
      }
      if (urlQueryParameters.new === 'true') {
        initPromise.then((data) => {
          clearNewUrlParameter();
          openNewAndEditCollectionModal(typeof data === 'object' ? { teamsList: data.teams, agencyConfiguration } : {});
        });
      }
    } else {
      setAgencyConfiguration({});
      setTeams([]);
      setLoading((prevState) => ({ ...prevState, agencies: false, teams: false }));
      setSearchPage(1);
    }
  }, [account.data]);

  useEffect(() => {
    // skip on first render - only run when the langauge value actually changes
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      if (teamHelpers.userHasTeams(user, agencyConfiguration)) {
        setLoading((prevState) => ({ ...prevState, teams: true }));
        loadTeams();
      }
    }
  }, [currentLanguage]);

  useEffect(() => {
    if (account.state.isLoggedIn || showCollectionFor === 'org') {
      loadCollections(searchPage);
    } else {
      setCollections([]);
      setLoading((prevState) => ({ ...prevState, collections: false }));
    }
  }, [account.state.isLoggedIn, searchPage, showCollectionFor, teamId, organizationId, router.query.q]);

  useEffect(() => {
    loadPageInfo();
  }, [user, showCollectionFor, teamId, organizationId, organization]);

  useEffect(() => {
    setShowCollectionFor(router.query.page || 'my');
    let orgId = parseIntOrFalse(router.query.organization_id) || organizationId;
    switch (router.query.page) {
      case 'team':
        setTeamId(parseIntOrFalse(router.query.organization_id) || teamId);
        setOrganizationId(null);
        break;
      case 'agency':
        setOrganizationId(orgId);
        setTeamId(null);
        setLoading((prevState) => ({ ...prevState, org: false }));
        break;
      case 'org':
        setOrganizationId(orgId);
        if (orgId) {
          loadOrganization(orgId);
          setLoading((prevState) => ({ ...prevState, agencies: false, teams: false, org: true }));
        }
        setTeamId(null);
        break;
      default:
        setOrganizationId(null);
        setTeamId(null);
        break;
    }
    if (router.query.sortby && router.query.sortby !== selectedSortBy) {
      handleSortChange({ sortBy: router.query.sortby });
    } else {
      setSearchPage(1);
    }
  }, [router.query.page, router.query.organization_id, router.query.sortby]);

  const loadOrganization = (id) => {
    ODRS.getResourceById('organizations', id, { locale: currentLanguage }).then((resp) => {
      setOrganization(resp.data);
      setLoading((prevState) => ({ ...prevState, org: false }));
    });
  };

  const isShowingMyCollections = () => {
    return showCollectionFor !== 'org' || (showCollectionFor === 'org' && organizationId === user?.affiliation?.fetchable_id);
  };

  const loadPageInfo = () => {
    let collectionHeaderInfo = {};
    switch (showCollectionFor) {
      case 'team':
        collectionHeaderInfo = {
          title: 'Group Collections',
          collectionFor: {
            name: teams?.filter((team) => team.id === teamId)[0].name,
            url: null
          }
        };
        break;
      case 'org':
        collectionHeaderInfo = {
          title: 'Organization Collections',
          collectionFor: {
            name: organization?.name,
            url: '/org/' + organization?.slug
          }
        };
        break;
      case 'agency':
        collectionHeaderInfo = {
          title: 'Facility Collections',
          collectionFor: {
            name: agencyConfiguration?.child_agencies?.filter((agency) => agency.id === organizationId)[0].name,
            url: '/org/' + organizationId
          }
        };
        break;
      case 'my':
        collectionHeaderInfo = {
          title: !!user?.is_professional ? 'My Collections' : 'Saved Collections',
          collectionFor: {
            name: hasName(user) ? displayName(user) : '',
            url: null
          }
        };
        break;
    }
    setPageInfo(collectionHeaderInfo);
  };

  const changePage = (selectedPage) => {
    setSearchPage(selectedPage);
    pageRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAgencyConfiguration = () => {
    return AgencyConfiguration.loadAgencyConfiguration().then((resp) => {
      setAgencyConfiguration(resp?.data?.agency_configuration);
      setLoading((prevState) => ({ ...prevState, agencies: false }));
      return resp?.data?.agency_configuration;
    });
  };

  const loadTeams = () => {
    return Team.getTeams({ locale: currentLanguage }).then((resp) => {
      if (resp) {
        setTeams(resp?.data?.teams);
      }
      setLoading((prevState) => ({ ...prevState, teams: false }));
      return resp?.data?.teams;
    });
  };

  const loadCollections = (page) => {
    setLoading((prevState) => ({ ...prevState, collections: true }));
    let params = {
      show_permissions_for_user: true,
      show_team_ids: true,
      include_owner_org: true,
      get_counts: true,
      limit: 0,
      list_only: false,
      page: page
    };
    if (router.query) {
      params.query = {};
      params.query.text = router.query.q;
      params.query.lat = router.query.lat;
      params.query.long = router.query.long;
      params.query.sortby = router.query.sortby || 'alphabetical';
    }
    let additionalParams = {};
    let requestFunction = 'loadMyCollections';
    let requestKey = showCollectionFor + '-' + (account?.state?.isLoggedIn && !isEmpty(account?.data) ? account.data.id : '');
    switch (showCollectionFor) {
      case 'org':
      case 'agency':
        additionalParams.organization_id = organizationId;
        requestFunction = 'loadOrgCollections';
        requestKey += '-' + organizationId?.toString();
        break;
      case 'team':
        additionalParams.team_id = teamId;
        requestFunction = 'loadTeamCollections';
        requestKey += '-' + teamId.toString();
        break;
    }

    params = { ...params, ...additionalParams };
    additionalParams.count_only = true;
    // create another simple request to get the total collections for the current showCollectionFor
    // skip if the page hasn't changed or if query is empty
    if (lastRequestFor.current.key !== requestKey) {
      const countTimestamp = Date.now();
      lastRequestFor.current = { key: requestKey, timestamp: countTimestamp };
      Collection[requestFunction](additionalParams).then((resp) => {
        if (resp && lastRequestFor.current.timestamp === countTimestamp) {
          setTotalCollections(resp?.data?.collection_count);
        }
      });
    }

    // Use a reference to track when was the last request to avoid slow previous API responses from overwriting
    // the state out of order.
    // TODO: Consider extending the API interface to have an option to cancel old, pending requests
    const currentTimestamp = Date.now();
    lastRequestAt.current = currentTimestamp;
    Collection[requestFunction](params).then((resp) => {
      if (resp && lastRequestAt.current === currentTimestamp) {
        setCollections(resp?.data?.collections);
        setPagination(resp?.data?.pagination);
        sortCollection(selectedSortBy, resp?.data?.collections);
        setLoading((prevState) => ({ ...prevState, collections: false }));
      }
    });
  };

  const handleCreateCollection = (collectionData) => {
    router.push('/collections/' + collectionData.collection.slug);
  };

  const handleCollectionChange = (collectionData) => {
    setSearchPage(1);
    loadCollections(1);
  };

  const handleSortChange = ({ sortBy }) => {
    setSelectedSortBy(sortBy);
    sortCollection(sortBy, collections);
  };

  const handleSearchChange = ({ q }) => {
    if (router.query.q !== q) {
      setSearchPage(1);
    }
  };

  const sortCollection = (sortBy, collectionsData) => {
    if (sortBy === '' || sortBy === undefined || collectionsData === undefined) {
      return;
    }
    setCollections(collectionsData);
  };

  const openNewAndEditCollectionModal = (realtimeData = {}) => {
    modal.enqueueModal(
      Object.assign(
        {},
        {
          name: 'NewAndEditCollectionModal',
          editCollection: false,
          agencyConfiguration: agencyConfiguration,
          teamsList: teams,
          onCreateCollection: handleCreateCollection
        },
        realtimeData
      )
    );
  };

  const getSelectedCollectionSubItem = () => {
    switch (showCollectionFor) {
      case 'agency':
        return find(agencyConfiguration.child_agencies, { id: organizationId });
        break;
      case 'team':
        return find(teams, { id: teamId });
        break;
      default:
        return false;
        break;
    }
  };

  const renderCreateNewCollectionButton = () => {
    return (
      <div className={classes.createNewCollectionButton}>
        <Button
          dataCy={'button-top-create'}
          type="primary"
          icon={{ style: 'fas', font: 'plus' }}
          onButtonClick={openNewAndEditCollectionModal}
          text={t('Create new collection')}
          styles={{ width: 'full' }}
        />
      </div>
    );
  };

  const TitleContent = ({ ...props }) => {
    return (
      <Row className={classes.collectionTitleRow}>
        <Col>
          <h5>
            <FontAwesomeIcon icon={['fas', 'book']} className={classes.titleIcon} />
            &nbsp;&nbsp;
            {t(pageInfo.title)}
          </h5>
          <p>
            <span>
              {!pageInfo.collectionFor.url && <span>{pageInfo.collectionFor.name}</span>}
              {pageInfo.collectionFor.url && (
                <span data-cy="text-owner">
                  <a href={pageInfo.collectionFor.url}>{pageInfo.collectionFor.name}</a>
                </span>
              )}
              {pageInfo.collectionFor.name && (account?.state?.isLoggedIn || showCollectionFor === 'org') && (
                <span data-cy="text-owner">&nbsp;&middot;&nbsp;</span>
              )}
              {(account?.state?.isLoggedIn || showCollectionFor === 'org') && (
                <>
                  <span>
                    <span>
                      {totalCollections} {totalCollections === 1 ? t('Collection') : t('Collections')}
                    </span>
                  </span>
                </>
              )}
            </span>
          </p>
        </Col>
      </Row>
    );
  };

  const selectedSubItem = getSelectedCollectionSubItem();

  const goToBrowseResources = () => {
    window.location.href = '/search';
  };
  return (
    <>
      {isFullPage && (
        <div className={classes.titleContainer}>
          <Container>
            <Row>
              <Col>
                <TitleContent />
              </Col>
            </Row>
          </Container>
        </div>
      )}
      <div className={isFullPage ? classes.bodyContainer : null}>
        {(account.state.isLoggedIn || showCollectionFor === 'org') && (
          <Container>
            {!isFullPage && (
              <Row className="p0">
                <Col>
                  <div className={`text-center ${classes.collectionCount}`}>
                    <TitleContent />
                  </div>
                </Col>
              </Row>
            )}
            <Row>
              {account?.data?.affiliation && isShowingMyCollections() && isMobileOrTablet && (
                <Col xs="12" lg="3" className={classes.collectionContainer}>
                  {loading.teams || loading.org || loading.agencies ? (
                    <Skeleton height={500} highlightColor={'#ffffff'} />
                  ) : (
                    <CollectionTypeSelectorList
                      activeItem={selectedSubItem}
                      activeSection={showCollectionFor}
                      agencies={agencyConfiguration?.child_agencies?.length > 0 ? agencyConfiguration.child_agencies : []}
                      teams={teams}
                      user={user}
                      sortBy={urlQueryParameters?.sortby}
                    />
                  )}
                </Col>
              )}
              <Col xs="12" lg="9" ref={pageRef} className={classes.collectionContainer}>
                <CollectionSearch
                  onSort={handleSortChange}
                  onSearch={handleSearchChange}
                  recordCount={pagination?.total_count}
                  urlQueryParameters={urlQueryParameters}
                />
              </Col>
              <Col xs="12" lg="3" className={classes.collectionContainer}>
                {renderCreateNewCollectionButton()}
              </Col>
            </Row>
            <Row>
              <Col
                xs="12"
                lg={account?.data?.affiliation && isShowingMyCollections() ? '9' : '12'}
                ref={pageRef}
                className={classes.collectionContainer}
              >
                {loading.collections ? (
                  <Skeleton height={500} highlightColor={'#ffffff'} />
                ) : (
                  <div>
                    {collections?.map((collection, index) => (
                      <React.Fragment key={index}>
                        <CollectionCard
                          collection={collection}
                          collectionsList={collections}
                          agencyConfiguration={agencyConfiguration}
                          teamsList={teams}
                          onDeleteCollection={handleCollectionChange}
                          onUpdateCollection={handleCollectionChange}
                        />
                      </React.Fragment>
                    ))}
                    <div data-cy="button-bottom-create-collection" onClick={openNewAndEditCollectionModal} className={`${classes.ghostCard} card`}>
                      <div className={classes.collectionLinkContainer}>
                        <div className={classes.collectionLink}>
                          <FontAwesomeIcon icon={['fas', 'plus']} />
                          <br />
                          {t('Create new collection')}
                        </div>
                      </div>
                    </div>
                    <br />
                    {!!pagination?.total_pages && (
                      <div className={classes.paginationBox}>
                        <TablePagination pagination={pagination} onPageChange={changePage} />
                      </div>
                    )}
                  </div>
                )}
              </Col>
              {account?.data?.affiliation && isShowingMyCollections() && isDesktop && (
                <Col xs="12" lg="3" className={classes.collectionContainer}>
                  {loading.teams || loading.org || loading.agencies ? (
                    <Skeleton height={500} highlightColor={'#ffffff'} />
                  ) : (
                    <CollectionTypeSelectorList
                      activeItem={selectedSubItem}
                      activeSection={showCollectionFor}
                      agencies={agencyConfiguration?.child_agencies?.length > 0 ? agencyConfiguration.child_agencies : []}
                      teams={teams}
                      user={user}
                      sortBy={urlQueryParameters?.sortby}
                    />
                  )}
                </Col>
              )}
            </Row>
          </Container>
        )}
        {!account.state.isLoggedIn && showCollectionFor !== 'org' && (
          <Container>
            <Row>
              <Col>
                <Card className={'p-3'}>
                  <h5>{t('Save and organize your important resources with Collections')}</h5>
                  <p>
                    {t('When you see a resource you want to add to a Collection, click the book icon')} <FontAwesomeIcon icon={'book'} />.
                  </p>
                  <div className={'text-center'}>
                    <Button
                      type="link"
                      icon={{ style: 'fas', font: 'search' }}
                      styles={{ width: isMobileOrTablet ? 'full' : '' }}
                      text={t('Click here to find resources')}
                      onButtonClick={() => goToBrowseResources()}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </Container>
        )}
      </div>
    </>
  );
};

export default Collections;
